import {
    IDataFlowDataType, DecimalDataFlowType, BooleanDataFlowType, IntegerDataFlowType, StringDataFlowType, NullDataFlowType, ArrayDataFlowType, AnyDataFlowType, LongDataFlowType
} from 'app/model/dataflow/dataflow-datatype';
import { Expression, IntegerLiteral, ColumnOperand, FunctionExpression, Literal, StringLiteral } from './expressionTypes';

import { format } from 'app/shared/utils/string.util';
import { DataFlowFunctionArgument } from './dataflow-function-argument';
import { IErrorExpression } from './IErrorExpression';
import { DataFlowTypesUtils } from './dataflow-type-utilities';
import { DataFlowSink } from './dataflow-sink';
import { MapDataFlowType } from 'worker/app-workers/shared/models/dataflow-datatype';
import { functionsDescriptionMap } from './dataflow-expression-functions-map';
import { ComplexDataFlowType, ComplexField } from './dataflow-complex-datatype';
import { DataFlowExpression } from './dataflowexpression';

export class DataFlowExpressionFunctions {
    public static IS_NULL: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isNull',
        'logical',
        false
    );

    public static IS_BOOLEAN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isBoolean',
        'logical',
        false
    );

    public static IS_SHORT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isShort',
        'logical',
        true
    );

    public static IS_INTEGER: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isInteger',
        'logical',
        true
    );

    public static IS_LONG: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isLong',
        'logical',
        true
    );

    public static IS_FLOAT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isFloat',
        'logical',
        true
    );

    public static IS_DOUBLE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isDouble',
        'logical',
        true
    );

    public static IS_BYTE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isByte',
        'logical',
        true
    );

    public static IS_DECIMAL: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isDecimal',
        'logical',
        true
    );

    public static IS_DATE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isDate',
        'logical',
        true
    );

    public static IS_TIMESTAMP: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isTimestamp',
        'logical',
        true
    );

    public static IS_NAN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isNan',
        'logical',
        false
    );

    public static EXPR: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'expr',
        'all',
        false
    );

    public static BITWISE_AND: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'bitwiseAnd',
        'math',
        true
    );

    public static BITWISE_OR: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'bitwiseOr',
        'math',
        true
    );

    public static BITWISE_XOR: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'bitwiseXor',
        'math',
        true
    );

    public static TO_BITSET: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'toBitSet',
        'math',
        true
    );

    public static IS_BITSET: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isBitSet',
        'logical',
        true
    );

    public static IS_ASSIGNABLE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isAssignable',
        'logical',
        false
    );

    public static SET_BITSET: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'setBitSet',
        'math',
        true
    );

    public static BETWEEN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'between',
        'math',
        true
    );

    public static IS_DISTINCT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isDistinct',
        'math',
        true
    );

    public static IIF_NULL: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'iifNull',
        'logical',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.ARGUMENTS_TYPECOMPATIBLE_VALIDATION
        }
    );

    public static NULL: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'null',
        'logical',
        false
    );

    public static IIF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'iif',
        'logical',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.LOGICAL_IIF_HANDLER,
            validateArgs: DataFlowExpressionFunctions.LOGICAL_IIF_VALIDATOR
        }
    );

    public static CASE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'case',
        'logical',
        true,
        {
            outputFilter: DataFlowExpressionFunctions.LOGICAL_CASE_HANDLER,
            validateArgs: DataFlowExpressionFunctions.LOGICAL_CASE_VALIDATOR
        }
    );

    public static COALESCE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'coalesce',
        'logical',
        true
    );

    public static TRUE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'true',
        'logical',
        false
    );

    public static FALSE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'false',
        'logical',
        false
    );

    public static CONCAT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'concat',
        'string',
        true
    );

    public static CONCAT_WS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'concatWS',
        'string',
        true
    );

    public static TYPE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'type',
        'logical',
        false
    );

    public static TRIM: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'trim',
        'string',
        false
    );

    public static LTRIM: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'ltrim',
        'string',
        false
    );

    public static RTRIM: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'rtrim',
        'string',
        false
    );

    public static UPPER: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'upper',
        'string',
        false
    );

    public static LOWER: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'lower',
        'string',
        false
    );

    public static SUBSTRING: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'substring',
        'string',
        false
    );

    public static SUBSTRING_INDEX: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'substringIndex',
        'string',
        false
    );

    public static DROP_LEFT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'dropLeft',
        'string',
        false
    );

    public static DROP_RIGHT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'dropRight',
        'string',
        false
    );

    public static JAROWINKLER: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'jaroWinkler',
        'string',
        false
    );

    public static TRANSLATE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'translate',
        'string',
        false
    );

    public static REVERSE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'reverse',
        'string',
        false
    );

    public static INITCAP: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'initCap',
        'string',
        false
    );

    public static LEFT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'left',
        'string',
        false
    );

    public static RIGHT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'right',
        'string',
        false
    );

    public static LENGTH: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'length',
        'string',
        false
    );

    public static SPLIT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'split',
        'string',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.SPLIT_ARRAY_HANDLER
        }
    );

    public static REGEX_SPLIT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'regexSplit',
        'string',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.SPLIT_ARRAY_HANDLER
        }
    );

    public static LPAD: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'lpad',
        'string',
        false
    );

    public static RPAD: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'rpad',
        'string',
        false
    );

    public static STARTS_WITH: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'startsWith',
        'string',
        false
    );

    public static ENDS_WITH: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'endsWith',
        'string',
        false
    );

    public static LOCATE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'locate',
        'string',
        false
    );

    public static INSTR: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'instr',
        'string',
        false
    );

    public static REPLACE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'replace',
        'string',
        false
    );

    public static REGEX_REPLACE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'regexReplace',
        'string',
        false
    );

    public static REGEX_EXTRACT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'regexExtract',
        'string',
        false
    );

    public static REGEX_MATCH: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'regexMatch',
        'string',
        false
    );

    public static LIKE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'like',
        'string',
        false
    );

    public static RLIKE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'rlike',
        'string',
        false
    );

    public static IN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'in',
        'string',
        false
    );

    public static SOUNDEX: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'soundex',
        'string',
        false
    );

    public static LEVENSHTEIN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'levenshtein',
        'string',
        false
    );

    public static NORMALIZE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'normalize',
        'string',
        false
    );

    public static DOUBLETOSINGLECHARACTERSET: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'doubleToSingleCharacterSet',
        'string',
        false
    );

    public static DECODE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'decode',
        'string',
        true
    );

    public static ENCODE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'encode',
        'string',
        true
    );

    public static CHAR: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'char',
        'string',
        true
    );

    public static ASCII: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'ascii',
        'string',
        true
    );

    public static ESCAPE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'escape',
        'string',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.ESCAPE_UNESCAPE_VALIDATION
        }
    );

    public static UNESCAPE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'unescape',
        'string',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.ESCAPE_UNESCAPE_VALIDATION
        }
    );

    public static AT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'at',
        'array',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.AT_ARGUMENTS_VALIDATION,
            outputFilter: DataFlowExpressionFunctions.AT_OUTPUT_FILTER
        }
    );

    public static ASSOCIATE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'associate',
        'array',
        true,
        {
            validateArgs: DataFlowExpressionFunctions.ASSOCIATE_ARGUMENTS_VALIDATION,
            outputFilter: DataFlowExpressionFunctions.ASSOCIATE_OUTPUT_FILTER
        }
    );

    public static REASSOCIATE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'reassociate',
        'array',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.REASSOCIATE_OUTPUT_FILTER
        }
    );

    public static ARRAY: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'array',
        'array',
        true,
        {
            validateArgs: DataFlowExpressionFunctions.ARGUMENTS_TYPEMATCH_VALIDATION,
            outputFilter: DataFlowExpressionFunctions.ARRAY_OUTPUT_FILTER
        }
    );

    public static DISTINCT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'distinct',
        'array',
        false
    );

    public static EXCEPT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'except',
        'array',
        false
    );

    public static INTERSECT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'intersect',
        'array',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.ARRAY_TYPEMATCH_VALIDATION
        }
    );

    public static UNION: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'union',
        'array',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.ARRAY_TYPEMATCH_VALIDATION
        }
    );

    public static FLATTEN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'flatten',
        'array',
        true,
        {
            validateArgs: DataFlowExpressionFunctions.FLATTEN_ARGUMENTS_VALIDATION
        }
    );

    public static UNFOLD: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'unfold',
        'array',
        false
    );

    public static TO_BASE64: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'toBase64',
        'string',
        false
    );

    public static FROM_BASE64: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'fromBase64',
        'string',
        false
    );

    public static TO_BINARY: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'toBinary',
        'string',
        false
    );

    public static TO_STRING: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'toString',
        'string',
        false
    );

    public static TO_BOOLEAN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'toBoolean',
        'logical',
        false
    );

    public static TO_BYTE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'toByte',
        'math',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_NUMERIC_CONVERSION_FUNCTION(false, 'toByte')
        }
    );

    public static TO_SHORT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'toShort',
        'math',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_NUMERIC_CONVERSION_FUNCTION(false, 'toShort')
        }
    );

    public static TO_INTEGER: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'toInteger',
        'math',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_NUMERIC_CONVERSION_FUNCTION(false, 'toInteger')
        }
    );

    public static TO_LONG: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'toLong',
        'math',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_NUMERIC_CONVERSION_FUNCTION(true, 'toLong')
        }
    );

    public static TO_FLOAT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'toFloat',
        'math',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_NUMERIC_CONVERSION_FUNCTION(false, 'toFloat')
        }
    );

    public static TO_DOUBLE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'toDouble',
        'math',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_NUMERIC_CONVERSION_FUNCTION(false, 'toDouble')
        }
    );

    public static TO_DECIMAL: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'toDecimal',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_CONVERT_TO_DECIMAL
        }
    );

    public static HEX: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'hex',
        'string',
        false
    );

    public static UNHEX: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'unhex',
        'string',
        false
    );

    public static ADD: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'add',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_ADD_TYPE_HANDLER,
            validateArgs: DataFlowExpressionFunctions.MATH_ADD_TYPE_VALIDATE
        }
    );

    public static MINUS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'minus',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_MINUS_TYPE_HANDLER,
            validateArgs: DataFlowExpressionFunctions.MATH_MINUS_TYPE_VALIDATE
        }
    );

    public static MULTIPLY: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'multiply',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_GET_WIDEST_NUMERIC_TYPE,
            validateArgs: DataFlowExpressionFunctions.VALIDATE_MUL_DIV_MOD_ARGS
        }
    );

    public static DIVIDE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'divide',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_GET_WIDEST_NUMERIC_TYPE,
            validateArgs: DataFlowExpressionFunctions.VALIDATE_MUL_DIV_MOD_ARGS
        }
    );

    public static MOD: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'mod',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_GET_WIDEST_NUMERIC_TYPE,
            validateArgs: DataFlowExpressionFunctions.VALIDATE_MUL_DIV_MOD_ARGS
        }
    );

    public static PMOD: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'pMod',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_GET_WIDEST_NUMERIC_TYPE,
            validateArgs: DataFlowExpressionFunctions.VALIDATE_MUL_DIV_MOD_ARGS
        }
    );

    public static ABS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'abs',
        'math'
    );

    public static AND: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'and',
        'logical'
    );

    public static OR: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'or',
        'logical'
    );

    public static XOR: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'xor',
        'logical'
    );

    public static NOT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'not',
        'logical'
    );

    public static EQUALS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'equals',
        'logical',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_EQUALITY_TYPE_ARGS
        }
    );

    public static NOT_EQUALS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'notEquals',
        'logical',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_EQUALITY_TYPE_ARGS
        }
    );

    public static EQUALS_IGNORE_CASE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'equalsIgnoreCase',
        'logical'
    );

    public static GREATER: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'greater',
        'logical',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_COMPARE_TYPE_ARGS
        }
    );

    public static LESSER: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'lesser',
        'logical',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_COMPARE_TYPE_ARGS
        }
    );

    public static GREATER_EQUAL: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'greaterOrEqual',
        'logical',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_COMPARE_TYPE_ARGS
        }
    );

    public static LESSER_EQUAL: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'lesserOrEqual',
        'logical',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_COMPARE_TYPE_ARGS
        }
    );

    public static GREATEST: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'greatest',
        'logical',
        true,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_COMPARE_TYPE_ARGS
        }
    );

    public static LEAST: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'least',
        'logical',
        true,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_COMPARE_TYPE_ARGS
        }
    );

    public static POWER: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'power',
        'math'
    );

    public static SQRT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'sqrt',
        'math'
    );

    public static NEGATE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'negate',
        'math'
    );

    public static COS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'cos',
        'math'
    );

    public static ACOS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'acos',
        'math'
    );

    public static COSH: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'cosh',
        'math'
    );

    public static SIN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'sin',
        'math'
    );

    public static ASIN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'asin',
        'math'
    );

    public static SINH: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'sinh',
        'math'
    );

    public static TAN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'tan',
        'math'
    );

    public static ATAN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'atan',
        'math'
    );

    public static TANH: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'tanh',
        'math'
    );

    public static ATAN2: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'atan2',
        'math'
    );

    public static CBRT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'cbrt',
        'math'
    );

    public static FACTORIAL: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'factorial',
        'math'
    );

    public static FLOOR: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'floor',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_FORCE_LONG
        }
    );

    public static CEIL: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'ceil',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_FORCE_LONG
        }
    );

    public static DEGREES: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'degrees',
        'math'
    );

    public static RADIANS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'radians',
        'math',
        false
    );

    public static LOG: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'log',
        'math',
        false
    );

    public static LOG10: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'log10',
        'math'
    );

    public static ROUND: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'round',
        'math',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_ROUND_MODE_OPTIONS
        }
    );

    public static CURRENT_DATE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'currentDate',
        'date',
        false
    );

    public static CURRENT_TIMESTAMP: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'currentTimestamp',
        'date'
    );

    public static TO_UTC: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'toUTC',
        'date',
        false
    );

    public static FROM_UTC: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'fromUTC',
        'date',
        false
    );

    public static CURRENT_UTC: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'currentUTC',
        'date',
        false
    );

    public static TO_DATE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'toDate',
        'date',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_TO_DATE_TIMESTAMP_HANDLER(true)
        }
    );

    public static TO_TIMESTAMP: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'toTimestamp',
        'date',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_TO_DATE_TIMESTAMP_HANDLER(false)
        }
    );

    public static MONTH: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'month',
        'date'
    );

    public static YEAR: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'year',
        'date'
    );

    public static HOUR: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'hour',
        'date',
        false
    );

    public static MINUTE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'minute',
        'date',
        false
    );

    public static SECOND: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'second',
        'date',
        false
    );

    public static MILLISECOND: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'millisecond',
        'date',
        false
    );

    public static DAY_OF_MONTH: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'dayOfMonth',
        'date',
        false
    );

    public static DAY_OF_WEEK: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'dayOfWeek',
        'date',
        false
    );

    public static DAY_OF_YEAR: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'dayOfYear',
        'date',
        false
    );

    public static WEEK_OF_YEAR: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'weekOfYear',
        'date',
        false
    );

    public static LAST_DAY_OF_MONTH: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'lastDayOfMonth',
        'date',
        false
    );

    public static MONTHS_BETWEEN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'monthsBetween',
        'date',
        false
    );

    public static ADD_MONTHS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'addMonths',
        'date',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.DATETIME_HANDLER
        }
    );

    public static SUB_MONTHS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'subMonths',
        'date',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.DATETIME_HANDLER
        }
    );

    public static ADD_DAYS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'addDays',
        'date',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.DATETIME_HANDLER
        }
    );

    public static SUB_DAYS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'subDays',
        'date',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.DATETIME_HANDLER
        }
    );

    public static WEEKS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'weeks',
        'date',
        false
    );

    public static DAYS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'days',
        'date',
        false
    );

    public static HOURS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'hours',
        'date',
        false
    );

    public static MINUTES: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'minutes',
        'date',
        false
    );

    public static SECONDS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'seconds',
        'date',
        false
    );

    public static MILLISECONDS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'milliseconds',
        'date',
        false
    );

    public static IS_INSERT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isInsert',
        'string',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.ROW_MATCHING_FUNCTION_VALIDATION
        }
    );

    public static IS_UPDATE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isUpdate',
        'string',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.ROW_MATCHING_FUNCTION_VALIDATION
        }
    );

    public static IS_UPSERT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isUpsert',
        'string',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.ROW_MATCHING_FUNCTION_VALIDATION
        }
    );

    public static IS_DELETE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isDelete',
        'string',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.ROW_MATCHING_FUNCTION_VALIDATION
        }
    );

    public static IS_MATCH: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isMatch',
        'string',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.ROW_MATCHING_FUNCTION_VALIDATION
        }
    );

    public static IS_ERROR: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isError',
        'string',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.ROW_MATCHING_FUNCTION_VALIDATION
        }
    );

    public static HAS_ERROR: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'hasError',
        'string',
        true
    );

    public static ASSERT_ERROR_MESSAGES: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'assertErrorMessages',
        'array',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.ASSERT_ERROR_MESSAGES_OUTPUT_FILTER
        }
    );

    public static IS_IGNORE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'isIgnore',
        'string',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.ROW_MATCHING_FUNCTION_VALIDATION
        }
    );

    public static NEXT_SEQUENCE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'nextSequence',
        'string',
        false
    );

    public static MD5: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'md5',
        'string',
        true
    );

    public static MD5_BINARY: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'md5Binary',
        'string',
        true
    );

    public static SHA1: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'sha1',
        'string',
        true
    );

    public static SHA1_BINARY: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'sha1Binary',
        'string',
        true
    );

    public static SHA2: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'sha2',
        'string',
        true
    );

    public static SHA2_BINARY: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'sha2Binary',
        'string',
        true
    );

    public static CRC32: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'crc32',
        'string',
        true
    );

    public static BLAKE2B: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'blake2b',
        'string',
        true
    );

    public static BLAKE2B_BINARY: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'blake2bBinary',
        'string',
        true
    );

    public static PARTITION_ID: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'partitionId',
        'string'
    );

    public static UNIQUE_IDENTIFIER: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'uuid',
        'string'
    );

    public static SLICE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'slice',
        'string',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.SLICE_ARRAY_HANDLER
        }
    );

    public static COMPARE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'compare',
        'logical'
    );

    public static BY_ORIGIN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'byOrigin',
        'logical',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.NO_COLUMN_ARG_VALIDATION.bind(DataFlowExpressionFunctions, true),
            validateParent: DataFlowExpressionFunctions.PARENT_IS_TYPECAST_VALIDATION
        }
    );

    public static BY_ORIGINS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'byOrigins',
        'logical',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.NO_COLUMN_ARG_VALIDATION.bind(DataFlowExpressionFunctions, true),
            validateParent: DataFlowExpressionFunctions.UNFOLDED_ARRAY_FUNCTION_VALIDATION
        }
    );

    public static BY_ITEM: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'byItem',
        'logical',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.NO_COLUMN_ARG_VALIDATION.bind(DataFlowExpressionFunctions, false)
        }
    );

    public static BY_NAME: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'byName',
        'logical',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.NO_COLUMN_ARG_VALIDATION.bind(DataFlowExpressionFunctions, true),
            validateParent: DataFlowExpressionFunctions.PARENT_IS_TYPECAST_VALIDATION
        }
    );

    public static BY_NAMES: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'byNames',
        'logical',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.NO_COLUMN_ARG_VALIDATION.bind(DataFlowExpressionFunctions, true),
            validateParent: DataFlowExpressionFunctions.UNFOLDED_ARRAY_FUNCTION_VALIDATION
        }
    );

    public static BY_POSITION: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'byPosition',
        'logical',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.BY_POSITION_FUNCTION_VALIDATION,
            validateParent: DataFlowExpressionFunctions.PARENT_IS_TYPECAST_VALIDATION
        }
    );

    public static BY_PATH: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'byPath',
        'logical',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.NO_COLUMN_ARG_VALIDATION.bind(DataFlowExpressionFunctions, true)
        }
    );

    public static HAS_PATH: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'hasPath',
        'logical'
    );

    public static HAS_COLUMN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'hasColumn',
        'logical'
    );

    public static COLUMNS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'columns',
        'array',
        false,
        {
            validateParent: DataFlowExpressionFunctions.UNFOLDED_ARRAY_FUNCTION_VALIDATION
        }
    );

    public static ORIGIN_COLUMNS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'originColumns',
        'logical',
        false,
        {
            validateParent: DataFlowExpressionFunctions.UNFOLDED_ARRAY_FUNCTION_VALIDATION
        }
    );

    public static COLUMN_NAMES: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'columnNames',
        'array'
    );

    public static RANDOM: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'random',
        'math'
    );

    public static LOOKUP: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'lookup',
        'array',
        true,
        {
            validateArgs: DataFlowExpressionFunctions.TRANSFORM_FUNCTION_LOOKUP_ARGS_VALIDATION.bind(DataFlowExpressionFunctions, 'lookup'),
            outputFilter: DataFlowExpressionFunctions.TRANSFORM_FUNCTION_SINGLE_HANDLER
        }
    );

    public static MLOOKUP: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'mlookup',
        'array',
        true,
        {
            validateArgs: DataFlowExpressionFunctions.TRANSFORM_FUNCTION_LOOKUP_ARGS_VALIDATION.bind(DataFlowExpressionFunctions, 'mlookup'),
            outputFilter: DataFlowExpressionFunctions.TRANSFORM_FUNCTION_SINGLE_HANDLER
        }
    );

    public static OUTPUTS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'outputs',
        'array',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.TRANSFORM_FUNCTION_OUTPUTS_ARGS_VALIDATION.bind(DataFlowExpressionFunctions, 'outputs'),
            outputFilter: DataFlowExpressionFunctions.TRANSFORM_FUNCTION_HANDLER
        }
    );

    public static OUTPUT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'output',
        'array',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.TRANSFORM_FUNCTION_OUTPUTS_ARGS_VALIDATION.bind(DataFlowExpressionFunctions, 'output'),
            outputFilter: DataFlowExpressionFunctions.TRANSFORM_FUNCTION_SINGLE_HANDLER
        }
    );

    public static SIZE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'size',
        'array'
    );

    public static FIND: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'find',
        'array',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.HIGHER_ORDER_SINGLE_HANDLER
        }
    );

    public static get ALL_FUNCTIONS(): DataFlowFunctionArgument[] {
        // When feature flag is cleaned, move functions to "ALL_FUNCTIONS_INTERNAL"
        const mapFunctions = [DataFlowExpressionFunctions.KEYS, DataFlowExpressionFunctions.VALUES, DataFlowExpressionFunctions.KEY_VALUES];
        return DataFlowExpressionFunctions.ALL_FUNCTIONS_INTERNAL.concat(DataFlowExpressionFunctions.HIGHER_ORDER_FUNCTIONS, DataFlowExpressionFunctions.TRANSFORM_FUNCTIONS).concat(mapFunctions);
    }

    public static ALL_FUNCTIONS_INTERNAL: DataFlowFunctionArgument[] = [
        DataFlowExpressionFunctions.IS_NULL,
        DataFlowExpressionFunctions.IS_BOOLEAN,
        DataFlowExpressionFunctions.IS_SHORT,
        DataFlowExpressionFunctions.IS_INTEGER,
        DataFlowExpressionFunctions.IS_LONG,
        DataFlowExpressionFunctions.IS_FLOAT,
        DataFlowExpressionFunctions.IS_DOUBLE,
        DataFlowExpressionFunctions.IS_BYTE,
        DataFlowExpressionFunctions.IS_DECIMAL,
        DataFlowExpressionFunctions.IS_DATE,
        DataFlowExpressionFunctions.IS_TIMESTAMP,
        DataFlowExpressionFunctions.IS_NAN,
        DataFlowExpressionFunctions.EXPR,
        DataFlowExpressionFunctions.BITWISE_AND,
        DataFlowExpressionFunctions.BITWISE_OR,
        DataFlowExpressionFunctions.BITWISE_XOR,
        DataFlowExpressionFunctions.TO_BITSET,
        DataFlowExpressionFunctions.IS_ASSIGNABLE,
        DataFlowExpressionFunctions.IS_BITSET,
        DataFlowExpressionFunctions.SET_BITSET,
        DataFlowExpressionFunctions.BETWEEN,
        DataFlowExpressionFunctions.IIF_NULL,
        DataFlowExpressionFunctions.NULL,
        DataFlowExpressionFunctions.IIF,
        DataFlowExpressionFunctions.CASE,
        DataFlowExpressionFunctions.COALESCE,
        DataFlowExpressionFunctions.TRUE,
        DataFlowExpressionFunctions.FALSE,
        DataFlowExpressionFunctions.CONCAT,
        DataFlowExpressionFunctions.CONCAT_WS,
        DataFlowExpressionFunctions.TYPE,
        DataFlowExpressionFunctions.TRIM,
        DataFlowExpressionFunctions.LTRIM,
        DataFlowExpressionFunctions.RTRIM,
        DataFlowExpressionFunctions.UPPER,
        DataFlowExpressionFunctions.LOWER,
        DataFlowExpressionFunctions.SUBSTRING,
        DataFlowExpressionFunctions.SUBSTRING_INDEX,
        DataFlowExpressionFunctions.DROP_LEFT,
        DataFlowExpressionFunctions.DROP_RIGHT,
        DataFlowExpressionFunctions.JAROWINKLER,
        DataFlowExpressionFunctions.TRANSLATE,
        DataFlowExpressionFunctions.REVERSE,
        DataFlowExpressionFunctions.INITCAP,
        DataFlowExpressionFunctions.LEFT,
        DataFlowExpressionFunctions.RIGHT,
        DataFlowExpressionFunctions.LENGTH,
        DataFlowExpressionFunctions.SPLIT,
        DataFlowExpressionFunctions.REGEX_SPLIT,
        DataFlowExpressionFunctions.LPAD,
        DataFlowExpressionFunctions.RPAD,
        DataFlowExpressionFunctions.STARTS_WITH,
        DataFlowExpressionFunctions.ENDS_WITH,
        DataFlowExpressionFunctions.LOCATE,
        DataFlowExpressionFunctions.INSTR,
        DataFlowExpressionFunctions.REPLACE,
        DataFlowExpressionFunctions.REGEX_REPLACE,
        DataFlowExpressionFunctions.REGEX_EXTRACT,
        DataFlowExpressionFunctions.REGEX_MATCH,
        DataFlowExpressionFunctions.LIKE,
        DataFlowExpressionFunctions.RLIKE,
        DataFlowExpressionFunctions.IN,
        DataFlowExpressionFunctions.SOUNDEX,
        DataFlowExpressionFunctions.LEVENSHTEIN,
        DataFlowExpressionFunctions.NORMALIZE,
        DataFlowExpressionFunctions.DOUBLETOSINGLECHARACTERSET,
        DataFlowExpressionFunctions.DECODE,
        DataFlowExpressionFunctions.ENCODE,
        DataFlowExpressionFunctions.CHAR,
        DataFlowExpressionFunctions.ASCII,
        DataFlowExpressionFunctions.ESCAPE,
        DataFlowExpressionFunctions.UNESCAPE,
        DataFlowExpressionFunctions.AT,
        DataFlowExpressionFunctions.ASSOCIATE,
        DataFlowExpressionFunctions.ARRAY,
        DataFlowExpressionFunctions.TO_BASE64,
        DataFlowExpressionFunctions.FROM_BASE64,
        DataFlowExpressionFunctions.TO_BINARY,
        DataFlowExpressionFunctions.TO_STRING,
        DataFlowExpressionFunctions.TO_BOOLEAN,
        DataFlowExpressionFunctions.TO_SHORT,
        DataFlowExpressionFunctions.TO_BYTE,
        DataFlowExpressionFunctions.TO_INTEGER,
        DataFlowExpressionFunctions.TO_LONG,
        DataFlowExpressionFunctions.TO_FLOAT,
        DataFlowExpressionFunctions.TO_DOUBLE,
        DataFlowExpressionFunctions.TO_DECIMAL,
        DataFlowExpressionFunctions.HEX,
        DataFlowExpressionFunctions.UNHEX,
        DataFlowExpressionFunctions.ADD,
        DataFlowExpressionFunctions.MINUS,
        DataFlowExpressionFunctions.MULTIPLY,
        DataFlowExpressionFunctions.DIVIDE,
        DataFlowExpressionFunctions.MOD,
        DataFlowExpressionFunctions.PMOD,
        DataFlowExpressionFunctions.ABS,
        DataFlowExpressionFunctions.AND,
        DataFlowExpressionFunctions.OR,
        DataFlowExpressionFunctions.XOR,
        DataFlowExpressionFunctions.NOT,
        DataFlowExpressionFunctions.EQUALS,
        DataFlowExpressionFunctions.NOT_EQUALS,
        DataFlowExpressionFunctions.EQUALS_IGNORE_CASE,
        DataFlowExpressionFunctions.GREATER,
        DataFlowExpressionFunctions.LESSER,
        DataFlowExpressionFunctions.GREATER_EQUAL,
        DataFlowExpressionFunctions.LESSER_EQUAL,
        DataFlowExpressionFunctions.GREATEST,
        DataFlowExpressionFunctions.LEAST,
        DataFlowExpressionFunctions.POWER,
        DataFlowExpressionFunctions.SQRT,
        DataFlowExpressionFunctions.NEGATE,
        DataFlowExpressionFunctions.COS,
        DataFlowExpressionFunctions.ACOS,
        DataFlowExpressionFunctions.COSH,
        DataFlowExpressionFunctions.SIN,
        DataFlowExpressionFunctions.ASIN,
        DataFlowExpressionFunctions.SINH,
        DataFlowExpressionFunctions.TAN,
        DataFlowExpressionFunctions.ATAN,
        DataFlowExpressionFunctions.TANH,
        DataFlowExpressionFunctions.ATAN2,
        DataFlowExpressionFunctions.CBRT,
        DataFlowExpressionFunctions.FACTORIAL,
        DataFlowExpressionFunctions.FLOOR,
        DataFlowExpressionFunctions.CEIL,
        DataFlowExpressionFunctions.DEGREES,
        DataFlowExpressionFunctions.RADIANS,
        DataFlowExpressionFunctions.LOG,
        DataFlowExpressionFunctions.LOG10,
        DataFlowExpressionFunctions.ROUND,
        DataFlowExpressionFunctions.CURRENT_DATE,
        DataFlowExpressionFunctions.CURRENT_TIMESTAMP,
        DataFlowExpressionFunctions.TO_UTC,
        DataFlowExpressionFunctions.FROM_UTC,
        DataFlowExpressionFunctions.CURRENT_UTC,
        DataFlowExpressionFunctions.TO_DATE,
        DataFlowExpressionFunctions.TO_TIMESTAMP,
        DataFlowExpressionFunctions.MONTH,
        DataFlowExpressionFunctions.YEAR,
        DataFlowExpressionFunctions.HOUR,
        DataFlowExpressionFunctions.MINUTE,
        DataFlowExpressionFunctions.SECOND,
        DataFlowExpressionFunctions.MILLISECOND,
        DataFlowExpressionFunctions.WEEKS,
        DataFlowExpressionFunctions.DAYS,
        DataFlowExpressionFunctions.HOURS,
        DataFlowExpressionFunctions.MINUTES,
        DataFlowExpressionFunctions.SECONDS,
        DataFlowExpressionFunctions.MILLISECONDS,
        DataFlowExpressionFunctions.DAY_OF_MONTH,
        DataFlowExpressionFunctions.DAY_OF_WEEK,
        DataFlowExpressionFunctions.DAY_OF_YEAR,
        DataFlowExpressionFunctions.WEEK_OF_YEAR,
        DataFlowExpressionFunctions.LAST_DAY_OF_MONTH,
        DataFlowExpressionFunctions.MONTHS_BETWEEN,
        DataFlowExpressionFunctions.ADD_MONTHS,
        DataFlowExpressionFunctions.SUB_MONTHS,
        DataFlowExpressionFunctions.ADD_DAYS,
        DataFlowExpressionFunctions.SUB_DAYS,
        DataFlowExpressionFunctions.IS_INSERT,
        DataFlowExpressionFunctions.IS_UPDATE,
        DataFlowExpressionFunctions.IS_UPSERT,
        DataFlowExpressionFunctions.IS_DELETE,
        DataFlowExpressionFunctions.IS_MATCH,
        DataFlowExpressionFunctions.IS_ERROR,
        DataFlowExpressionFunctions.HAS_ERROR,
        DataFlowExpressionFunctions.ASSERT_ERROR_MESSAGES,
        DataFlowExpressionFunctions.IS_IGNORE,
        DataFlowExpressionFunctions.NEXT_SEQUENCE,
        DataFlowExpressionFunctions.MD5,
        DataFlowExpressionFunctions.MD5_BINARY,
        DataFlowExpressionFunctions.SHA1,
        DataFlowExpressionFunctions.SHA1_BINARY,
        DataFlowExpressionFunctions.SHA2,
        DataFlowExpressionFunctions.SHA2_BINARY,
        DataFlowExpressionFunctions.CRC32,
        DataFlowExpressionFunctions.BLAKE2B,
        DataFlowExpressionFunctions.BLAKE2B_BINARY,
        DataFlowExpressionFunctions.PARTITION_ID,
        DataFlowExpressionFunctions.UNIQUE_IDENTIFIER,
        DataFlowExpressionFunctions.SLICE,
        DataFlowExpressionFunctions.COMPARE,
        DataFlowExpressionFunctions.BY_ORIGIN,
        DataFlowExpressionFunctions.BY_ORIGINS,
        DataFlowExpressionFunctions.BY_NAME,
        DataFlowExpressionFunctions.BY_ITEM,
        DataFlowExpressionFunctions.BY_NAMES,
        DataFlowExpressionFunctions.BY_POSITION,
        DataFlowExpressionFunctions.BY_PATH,
        DataFlowExpressionFunctions.HAS_PATH,
        DataFlowExpressionFunctions.HAS_COLUMN,
        DataFlowExpressionFunctions.COLUMNS,
        DataFlowExpressionFunctions.ORIGIN_COLUMNS,
        DataFlowExpressionFunctions.COLUMN_NAMES,
        DataFlowExpressionFunctions.RANDOM,
        DataFlowExpressionFunctions.SIZE,
        DataFlowExpressionFunctions.DISTINCT,
        DataFlowExpressionFunctions.EXCEPT,
        DataFlowExpressionFunctions.INTERSECT,
        DataFlowExpressionFunctions.UNION,
        DataFlowExpressionFunctions.FLATTEN,
        DataFlowExpressionFunctions.UNFOLD,
        DataFlowExpressionFunctions.IS_DISTINCT
    ];

    public static TRANSFORM_FUNCTIONS: DataFlowFunctionArgument[] = [
        DataFlowExpressionFunctions.LOOKUP,
        DataFlowExpressionFunctions.MLOOKUP,
        DataFlowExpressionFunctions.OUTPUTS,
        DataFlowExpressionFunctions.OUTPUT
    ];

    public static TYPE_CASTING_FUNCTIONS: DataFlowFunctionArgument[] = [
        DataFlowExpressionFunctions.TO_STRING,
        DataFlowExpressionFunctions.TO_SHORT,
        DataFlowExpressionFunctions.TO_BYTE,
        DataFlowExpressionFunctions.TO_INTEGER,
        DataFlowExpressionFunctions.TO_LONG,
        DataFlowExpressionFunctions.TO_FLOAT,
        DataFlowExpressionFunctions.TO_DOUBLE,
        DataFlowExpressionFunctions.TO_DECIMAL,
        DataFlowExpressionFunctions.TO_BOOLEAN,
        DataFlowExpressionFunctions.TO_BASE64,
        DataFlowExpressionFunctions.TO_BINARY,
        DataFlowExpressionFunctions.TO_UTC,
        DataFlowExpressionFunctions.TO_TIMESTAMP,
        DataFlowExpressionFunctions.TO_DATE,
        DataFlowExpressionFunctions.TO_BITSET
    ];

    public static DEBUG_TYPE_CASTING_FUNCTIONS: DataFlowFunctionArgument[] = [
        DataFlowExpressionFunctions.TO_STRING,
        DataFlowExpressionFunctions.TO_SHORT,
        DataFlowExpressionFunctions.TO_BYTE,
        DataFlowExpressionFunctions.TO_INTEGER,
        DataFlowExpressionFunctions.TO_LONG,
        DataFlowExpressionFunctions.TO_FLOAT,
        DataFlowExpressionFunctions.TO_DOUBLE,
        DataFlowExpressionFunctions.TO_DECIMAL,
        DataFlowExpressionFunctions.TO_BOOLEAN
    ];

    public static DEBUG_TRANSFORMATION_FUNCTIONS: DataFlowFunctionArgument[] = [
        DataFlowExpressionFunctions.UPPER,
        DataFlowExpressionFunctions.LOWER,
        DataFlowExpressionFunctions.TRIM,
        DataFlowExpressionFunctions.ROUND,
        DataFlowExpressionFunctions.FLOOR,
        DataFlowExpressionFunctions.CEIL,
        DataFlowExpressionFunctions.ABS,
        DataFlowExpressionFunctions.SQRT
    ];
    public static MAP: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'map',
        'array',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.HIGHER_ORDER_ARRAY_HANDLER
        }
    );

    public static MAP_ASSOCIATE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'mapAssociation',
        'array',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.HIGHER_ORDER_MAP_ASSOCIATE
        }
    );

    public static MAP_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'mapIf',
        'array',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.HIGHER_ORDER_ARRAY_HANDLER
        }
    );

    public static MAP_INDEX: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'mapIndex',
        'array',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.HIGHER_ORDER_ARRAY_HANDLER
        }
    );

    public static MAP_LOOP: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'mapLoop',
        'array',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.HIGHER_ORDER_MAP_LOOP
        }
    );

    public static KEYS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'keys',
        'array',
        false
    );

    public static VALUES: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'values',
        'array',
        false
    );

    public static KEY_VALUES: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'keyValues',
        'array',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.KEYVALUES_ARGUMENTS_VALIDATION,
            outputFilter: DataFlowExpressionFunctions.KEYVALUES_OUTPUT_FILTER
        }
    );

    public static REDUCE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'reduce',
        'array',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.REDUCE_HANDLER
        }
    );

    public static FILTER: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'filter',
        'array',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.HIGHER_ORDER_ARRAY_HANDLER
        }
    );

    public static CONTAINS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'contains',
        'array'
    );

    public static SORT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'sort',
        'array',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.HIGHER_ORDER_ARRAY_HANDLER
        }
    );

    public static HIGHER_ORDER_FUNCTIONS: DataFlowFunctionArgument[] = [
        DataFlowExpressionFunctions.MAP,
        DataFlowExpressionFunctions.MAP_ASSOCIATE,
        DataFlowExpressionFunctions.MAP_IF,
        DataFlowExpressionFunctions.MAP_INDEX,
        DataFlowExpressionFunctions.MAP_LOOP,
        DataFlowExpressionFunctions.REDUCE,
        DataFlowExpressionFunctions.FILTER,
        DataFlowExpressionFunctions.CONTAINS,
        DataFlowExpressionFunctions.SORT,
        DataFlowExpressionFunctions.FIND,
        DataFlowExpressionFunctions.REASSOCIATE
    ];

    public static MATCH_TYPE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'typeMatch',
        'string',
        false
    );

    public static PATTERN_FUNCTIONS = [
        DataFlowExpressionFunctions.MATCH_TYPE
    ];

    public static SUM: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'sum',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_FORCE_LONG_OR_DOUBLE,
            precisionAddition: 10,
            scaleAddition: 0
        }
    );

    public static SUM_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'sumIf',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_FORCE_LONG_OR_DOUBLE_IF,
            precisionAddition: 10,
            scaleAddition: 0
        }
    );

    public static SUM_DISTINCT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'sumDistinct',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_FORCE_LONG_OR_DOUBLE,
            precisionAddition: 10,
            scaleAddition: 0
        }
    );

    public static SUM_DISTINCT_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'sumDistinctIf',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_FORCE_LONG_OR_DOUBLE_IF,
            precisionAddition: 10,
            scaleAddition: 0
        }
    );

    public static COUNT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'count',
        'math'
    );

    public static COUNT_ALL: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'countAll',
        'math',
        true
    );

    public static COUNT_ALL_DISTINCT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'countAllDistinct',
        'math',
        true
    );

    public static COUNT_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'countIf',
        'math'
    );

    public static COUNT_DISTINCT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'countDistinct',
        'math',
        true
    );

    public static COUNT_DISTINCT_APPROXIMATE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'approxDistinctCount',
        'math',
        true
    );

    public static AVG: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'avg',
        'math',
        false,
        {
            // Sample scala logic: DecimalType => DecimalType(t.precision + 4, t.scale + 4)
            outputFilter: DataFlowExpressionFunctions.MATH_FORCE_DOUBLE,
            precisionAddition: 4,
            scaleAddition: 4
        }
    );

    public static AVG_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'avgIf',
        'math',
        false,
        {
            // Sample scala logic: DecimalType => DecimalType(t.precision + 4, t.scale + 4)
            outputFilter: DataFlowExpressionFunctions.MATH_FORCE_DOUBLE_IF,
            precisionAddition: 4,
            scaleAddition: 4
        }
    );

    public static MEAN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'mean',
        'math',
        false,
        {
            // Sample scala logic: DecimalType => DecimalType(t.precision + 4, t.scale + 4)
            outputFilter: DataFlowExpressionFunctions.MATH_FORCE_DOUBLE,
            precisionAddition: 4,
            scaleAddition: 4
        }
    );

    public static MEAN_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'meanIf',
        'math',
        false,
        {
            // Sample scala logic: DecimalType => DecimalType(t.precision + 4, t.scale + 4)
            outputFilter: DataFlowExpressionFunctions.MATH_FORCE_DOUBLE_IF,
            precisionAddition: 4,
            scaleAddition: 4
        }
    );

    public static MIN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'min',
        'math'
    );

    public static MIN_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'minIf',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.REDUCE_HANDLER
        }
    );

    public static MAX: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'max',
        'math'
    );

    public static MAX_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'maxIf',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.REDUCE_HANDLER
        }
    );

    public static STDDEV: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'stddev',
        'math'
    );

    public static STDDEV_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'stddevIf',
        'math'
    );

    public static STDDEV_POPULATION: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'stddevPopulation',
        'math'
    );

    public static STDDEV_POPULATION_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'stddevPopulationIf',
        'math'
    );

    public static STDDEV_SAMPLE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'stddevSample',
        'math'
    );

    public static STDDEV_SAMPLE_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'stddevSampleIf',
        'math'
    );

    public static VARIANCE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'variance',
        'math'
    );

    public static VARIANCE_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'varianceIf',
        'math'
    );

    public static VARIANCE_POPULATION: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'variancePopulation',
        'math'
    );

    public static VARIANCE_POPULATION_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'variancePopulationIf',
        'math'
    );

    public static VARIANCE_SAMPLE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'varianceSample',
        'math'
    );

    public static VARIANCE_SAMPLE_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'varianceSampleIf',
        'math'
    );

    public static COVARIANCE_POPULATION: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'covariancePopulation',
        'math'
    );

    public static COVARIANCE_POPULATION_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'covariancePopulationIf',
        'math'
    );

    public static COVARIANCE_SAMPLE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'covarianceSample',
        'math'
    );

    public static COVARIANCE_SAMPLE_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'covarianceSampleIf',
        'math'
    );

    public static KURTOSIS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'kurtosis',
        'math'
    );

    public static KURTOSIS_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'kurtosisIf',
        'math'
    );

    public static SKEWNESS: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'skewness',
        'math'
    );

    public static SKEWNESS_IF: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'skewnessIf',
        'math'
    );

    public static FIRST: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'first',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_FIRST_LAST_HANDLER
        }
    );

    public static LAST: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'last',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_FIRST_LAST_HANDLER
        }
    );

    public static COLLECT: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'collect',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_GET_COLLECT_TYPE
        }
    );

    public static COLLECT_UNIQUE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'collectUnique',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_GET_COLLECT_TYPE
        }
    );

    public static APPROX_PERCENTILE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'approxPercentile',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_GET_COLLECT_TYPE // same return type behavior as collect
        }
    );

    public static TOPN: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'topN',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_GET_TOPN_TYPE,
            validateArgs: DataFlowExpressionFunctions.VALIDATE_TOPN_ARGUMENTS
        }
    );

    public static AGGREGATE_FUNCTIONS: DataFlowFunctionArgument[] = [
        DataFlowExpressionFunctions.SUM,
        DataFlowExpressionFunctions.SUM_IF,
        DataFlowExpressionFunctions.SUM_DISTINCT,
        DataFlowExpressionFunctions.SUM_DISTINCT_IF,
        DataFlowExpressionFunctions.COUNT,
        DataFlowExpressionFunctions.COUNT_ALL,
        DataFlowExpressionFunctions.COUNT_ALL_DISTINCT,
        DataFlowExpressionFunctions.COUNT_IF,
        DataFlowExpressionFunctions.COUNT_DISTINCT,
        DataFlowExpressionFunctions.COUNT_DISTINCT_APPROXIMATE,
        DataFlowExpressionFunctions.IS_DISTINCT,
        DataFlowExpressionFunctions.AVG,
        DataFlowExpressionFunctions.AVG_IF,
        DataFlowExpressionFunctions.MEAN,
        DataFlowExpressionFunctions.MEAN_IF,
        DataFlowExpressionFunctions.MIN,
        DataFlowExpressionFunctions.MIN_IF,
        DataFlowExpressionFunctions.MAX,
        DataFlowExpressionFunctions.MAX_IF,
        DataFlowExpressionFunctions.STDDEV,
        DataFlowExpressionFunctions.STDDEV_IF,
        DataFlowExpressionFunctions.STDDEV_POPULATION,
        DataFlowExpressionFunctions.STDDEV_POPULATION_IF,
        DataFlowExpressionFunctions.STDDEV_SAMPLE,
        DataFlowExpressionFunctions.STDDEV_SAMPLE_IF,
        DataFlowExpressionFunctions.VARIANCE,
        DataFlowExpressionFunctions.VARIANCE_IF,
        DataFlowExpressionFunctions.VARIANCE_POPULATION,
        DataFlowExpressionFunctions.VARIANCE_POPULATION_IF,
        DataFlowExpressionFunctions.VARIANCE_SAMPLE,
        DataFlowExpressionFunctions.VARIANCE_SAMPLE_IF,
        DataFlowExpressionFunctions.COVARIANCE_POPULATION,
        DataFlowExpressionFunctions.COVARIANCE_POPULATION_IF,
        DataFlowExpressionFunctions.COVARIANCE_SAMPLE,
        DataFlowExpressionFunctions.COVARIANCE_SAMPLE_IF,
        DataFlowExpressionFunctions.KURTOSIS,
        DataFlowExpressionFunctions.KURTOSIS_IF,
        DataFlowExpressionFunctions.SKEWNESS,
        DataFlowExpressionFunctions.SKEWNESS_IF,
        DataFlowExpressionFunctions.FIRST,
        DataFlowExpressionFunctions.LAST,
        DataFlowExpressionFunctions.COLLECT,
        DataFlowExpressionFunctions.COLLECT_UNIQUE,
        DataFlowExpressionFunctions.TOPN
    ];

    public static LAG: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'lag',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_FIRST_LAST_HANDLER
        }
    );

    public static LEAD: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'lead',
        'math',
        false,
        {
            outputFilter: DataFlowExpressionFunctions.MATH_FIRST_LAST_HANDLER
        }
    );

    public static CUMEDIST: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'cumeDist',
        'math'
    );

    public static NTILE: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'nTile',
        'math',
        false,
        {
            validateArgs: DataFlowExpressionFunctions.VALIDATE_FIRST_ARG_NO_COLUMN_REF
        }
    );

    public static RANK: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'rank',
        'math'
    );

    public static DENSE_RANK: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'denseRank',
        'math'
    );

    public static ROW_NUMBER: DataFlowFunctionArgument = new DataFlowFunctionArgument(
        'rowNumber',
        'math'
    );

    public static WINDOW_FUNCTIONS: DataFlowFunctionArgument[] = [
        DataFlowExpressionFunctions.LAG,
        DataFlowExpressionFunctions.LEAD,
        DataFlowExpressionFunctions.CUMEDIST,
        DataFlowExpressionFunctions.NTILE,
        DataFlowExpressionFunctions.RANK,
        DataFlowExpressionFunctions.DENSE_RANK,
        DataFlowExpressionFunctions.ROW_NUMBER
    ];

    public static ALL_INLCUDING_AGGREGATE_FUNCTIONS: DataFlowFunctionArgument[] = [].concat(...DataFlowExpressionFunctions.ALL_FUNCTIONS, DataFlowExpressionFunctions.AGGREGATE_FUNCTIONS);
    public static ALL_INLCUDING_WINDOW_FUNCTIONS: DataFlowFunctionArgument[] = [].concat(
        ...DataFlowExpressionFunctions.ALL_FUNCTIONS, DataFlowExpressionFunctions.AGGREGATE_FUNCTIONS, DataFlowExpressionFunctions.WINDOW_FUNCTIONS);
    public static ALL_INLCUDING_PATTERN_FUNCTIONS: DataFlowFunctionArgument[] = [].concat(
        ...DataFlowExpressionFunctions.ALL_FUNCTIONS, DataFlowExpressionFunctions.PATTERN_FUNCTIONS);

    public static ALL_FUNCTIONS_INCLUDING_PATTERN_WINDOW_AGGREGATE: DataFlowFunctionArgument[] = [].concat(
        ...DataFlowExpressionFunctions.ALL_FUNCTIONS, DataFlowExpressionFunctions.AGGREGATE_FUNCTIONS, ...DataFlowExpressionFunctions.WINDOW_FUNCTIONS, ...DataFlowExpressionFunctions.PATTERN_FUNCTIONS);

    public static ALL_FUNCTION_NAMES(): string[] {
        const functionNames: string [] = [];
        DataFlowExpressionFunctions.ALL_FUNCTIONS_INCLUDING_PATTERN_WINDOW_AGGREGATE.forEach((func) => {
            functionNames.push(func.name);
            if (functionsDescriptionMap[func.name]?.synonyms?.length > 0) {
                functionsDescriptionMap[func.name].synonyms.forEach((synonym: string) => functionNames.push(synonym));
            }
        });
        return functionNames;
    }

    // Output filter helper functions.

    public static MATH_FORCE_LONG_OR_DOUBLE_IF(expressions: Expression[], additionalPrecision?: number, additionalScale?: number): IDataFlowDataType {
        return DataFlowExpressionFunctions.MATH_FORCE_LONG_OR_DOUBLE(expressions.slice(1), additionalPrecision, additionalScale);
    }

    public static MATH_FORCE_LONG_OR_DOUBLE(expressions: Expression[], additionalPrecision?: number, additionalScale?: number): IDataFlowDataType {
        const inputArguments = expressions.map((e) => e.returnType);
        if (inputArguments.every((argument) => argument && DataFlowTypesUtils.isIntegerType(argument))) {
            return DataFlowTypesUtils.createType('long');
        } else if (inputArguments.every((argument) => argument && DataFlowTypesUtils.isNumberType(argument))) {
            if (inputArguments.some((argument) => argument && argument.typeString === 'decimal')) {
                return DataFlowExpressionFunctions.MATH_HANDLE_DECIMAL(inputArguments, additionalPrecision, additionalScale);
            }
            return DataFlowTypesUtils.createType('double');
        }
        return undefined;
    }

    public static MATH_FORCE_LONG(expressions: Expression[], additionalPrecision?: number, additionalScale?: number): IDataFlowDataType {
        const inputArguments = expressions.map((e) => e.returnType);
        if (inputArguments.every((argument) => argument && DataFlowTypesUtils.isNumberType(argument))) {
            if (inputArguments.some((argument) => argument && argument.typeString === 'decimal')) {
                return DataFlowExpressionFunctions.MATH_HANDLE_DECIMAL(inputArguments, additionalPrecision, additionalScale);
            }
            return DataFlowTypesUtils.createType('long');
        }
        return undefined;
    }

    public static MATH_FORCE_DOUBLE_IF(expressions: Expression[], additionalPrecision?: number, additionalScale?: number): IDataFlowDataType {
        return DataFlowExpressionFunctions.MATH_FORCE_DOUBLE(expressions.slice(1), additionalPrecision, additionalScale);
    }

    public static MATH_FORCE_DOUBLE(expressions: Expression[], additionalPrecision?: number, additionalScale?: number): IDataFlowDataType {
        const inputArguments = expressions.map((e) => e.returnType);
        if (inputArguments.every((argument) => argument && DataFlowTypesUtils.isNumberType(argument))) {
            if (inputArguments.some((argument) => argument && argument.typeString === 'decimal')) {
                return DataFlowExpressionFunctions.MATH_HANDLE_DECIMAL(inputArguments, additionalPrecision, additionalScale);
            }
            return DataFlowTypesUtils.createType('double');
        }
        return undefined;
    }

    public static MATH_HANDLE_DECIMAL(inputArguments: IDataFlowDataType[], additionalPrecision?: number, additionalScale?: number): IDataFlowDataType {
        const returnType = DataFlowTypesUtils.getWidestType(inputArguments) as DecimalDataFlowType;
        return additionalPrecision && additionalScale
            ? DataFlowExpressionFunctions.MATH_DEFAULT_HANDLER_FOR_PRECISION_AND_SCALE_DECIMAL(returnType)
            : DataFlowExpressionFunctions.MATH_INCREASE_PRECISION_AND_SCALE_DECIMAL(returnType, additionalPrecision, additionalScale);
    }

    public static MATH_DEFAULT_HANDLER_FOR_PRECISION_AND_SCALE_DECIMAL(decimalType: DecimalDataFlowType): IDataFlowDataType {
        if (decimalType) {
            if (decimalType.scale && decimalType.scale !== 0) {
                decimalType.precision = decimalType.precision - decimalType.scale + 1;
                decimalType.scale = 0;
            }
        }
        return decimalType;
    }

    public static MATH_INCREASE_PRECISION_AND_SCALE_DECIMAL(decimalType: DecimalDataFlowType, additionalPrecision: number, additionalScale: number): IDataFlowDataType {
        if (decimalType) {
            if (decimalType.precision) {
                decimalType.precision += additionalPrecision;
            }
            if (decimalType.scale) {
                decimalType.scale += additionalScale;
            }
        }
        return decimalType;
    }

    public static MATH_CONVERT_TO_DECIMAL(expressions: Expression[]): IDataFlowDataType {
        const decimalType = DataFlowTypesUtils.createType(DecimalDataFlowType.TYPE) as DecimalDataFlowType;

        if (expressions.length > 1 && expressions[1].literal && expressions[1].literal.value) {
            decimalType.precision = +(expressions[1].literal.value as IntegerLiteral).value.value;
        }

        if (expressions.length > 2 && expressions[2].literal && expressions[2].literal.value) {
            decimalType.scale = +(expressions[2].literal.value as IntegerLiteral).value.value;
        }

        return decimalType;
    }

    public static MATH_ADD_TYPE_HANDLER(expressions: Expression[]): IDataFlowDataType {
        const inputArguments = expressions.map((e) => e.returnType);
        if (inputArguments[0] && DataFlowTypesUtils.isDateTimeType(inputArguments[0]) && inputArguments[1] && DataFlowTypesUtils.isIntegerType(inputArguments[1])) {
            return DataFlowTypesUtils.createType(inputArguments[0].typeString);
        }
        return undefined;
    }

    public static MATH_MINUS_TYPE_HANDLER(expressions: Expression[]): IDataFlowDataType {
        const inputArguments = expressions.map((e) => e.returnType);
        const leftArg = inputArguments[0];
        const rightArg = inputArguments[1];
        if (leftArg && rightArg) {
            if (DataFlowTypesUtils.isDateTimeType(leftArg) && DataFlowTypesUtils.isNumberType(rightArg)) {
                return DataFlowTypesUtils.createType(leftArg.typeString);
            } else if (DataFlowTypesUtils.isDateType(leftArg) && DataFlowTypesUtils.isDateType(rightArg)) {
                return DataFlowTypesUtils.createType(IntegerDataFlowType.TYPE);
            } else if (DataFlowTypesUtils.isTimeStampType(leftArg) && DataFlowTypesUtils.isTimeStampType(rightArg)) {
                return DataFlowTypesUtils.createType(LongDataFlowType.TYPE);
            }
        }
        return undefined;
    }

    public static MATH_ADD_TYPE_VALIDATE(expressions: Expression[]): IErrorExpression[] {
        return DataFlowExpressionFunctions.VALIDATE_ADD_MINUS_ARGS(expressions, false);
    }

    public static MATH_MINUS_TYPE_VALIDATE(expressions: Expression[]): IErrorExpression[] {
        return DataFlowExpressionFunctions.VALIDATE_ADD_MINUS_ARGS(expressions, true);
    }

    public static MATH_FIRST_LAST_HANDLER(expressions: Expression[]): IDataFlowDataType {
        const inputArguments = expressions.map((e) => e.returnType);
        return inputArguments[0] || undefined;
    }

    public static MATH_GET_WIDEST_NUMERIC_TYPE(expressions: Expression[]): IDataFlowDataType {
        const inputArguments = expressions.map((e) => e.returnType);
        return DataFlowTypesUtils.getWidestType(inputArguments);
    }

    public static MATH_GET_COLLECT_TYPE(expressions: Expression[]): IDataFlowDataType {
        const arrayDataFlowType = new ArrayDataFlowType();
        const inputArguments = expressions.map((e) => e.returnType);
        arrayDataFlowType.arrayType = DataFlowTypesUtils.getWidestType(inputArguments);
        return arrayDataFlowType;
    }

    public static MATH_GET_TOPN_TYPE(expressions: Expression[]): IDataFlowDataType {
        const arrayDataFlowType = new ArrayDataFlowType();
        const inputArguments = expressions.map((e) => e.returnType);
        const complexType = new ComplexDataFlowType();
        const valueType = new DataFlowExpression();
        valueType.returnType = inputArguments[0] || undefined;
        complexType.fields.push(new ComplexField('value', valueType, complexType));
        const countType = new DataFlowExpression();
        countType.returnType = new LongDataFlowType();
        complexType.fields.push(new ComplexField('count', countType, complexType));
        arrayDataFlowType.arrayType = complexType;
        return arrayDataFlowType;
    }

    public static VALIDATE_TOPN_ARGUMENTS(expressions: Expression[]): IErrorExpression[] {
        const violations: IErrorExpression[] = [];
        const inputArguments = expressions.map((e) => e.returnType);
        if (inputArguments[1] && inputArguments[1].typeString) {
            const secondArgumentType = inputArguments[1].typeString;
            if (secondArgumentType !== LongDataFlowType.TYPE) {
                violations.push({
                    expression: expressions[1],
                    errorMessage: format(stringResources.DataFlowEditorErrorTypeMismatch, secondArgumentType, LongDataFlowType.TYPE),
                    treatErrorAsWarning: false
                });
            }
        }
        if (inputArguments[2] && inputArguments[2].typeString) {
            const thirdArgumentType = inputArguments[2].typeString;
            if (thirdArgumentType !== IntegerDataFlowType.TYPE) {
                violations.push({
                    expression: expressions[2],
                    errorMessage: format(stringResources.DataFlowEditorErrorTypeMismatch, thirdArgumentType, IntegerDataFlowType.TYPE),
                    treatErrorAsWarning: false
                });
            }
        }
        return violations;
    }

    public static VALIDATE_ROUND_MODE_OPTIONS(expressions: Expression[]): IErrorExpression[] {
        const violations: IErrorExpression[] = [];
        const inputArguments = expressions.map((e) => e.returnType);
        const isSupportedRoundModeForDecimalType = function(lit: Literal) {
            const supportedRoundModesForDecimalType = [3, 4, 5, 7];
            return lit.value && supportedRoundModesForDecimalType.includes(+(lit.value as IntegerLiteral).value.value);
        };

        if (DataFlowTypesUtils.isDecimalType(inputArguments[0])) {
            if (expressions.length > 2 && !isSupportedRoundModeForDecimalType(expressions[2].literal)) {
                violations.push({
                    expression: expressions[2],
                    errorMessage: format(stringResources.DataFlowRoundModeNotSupportedForDecimalTypeError, (expressions[2].literal.value as IntegerLiteral).value.value),
                    treatErrorAsWarning: false
                });
            }
        }
        return violations;
    }

    public static DATETIME_HANDLER(expressions: Expression[]): IDataFlowDataType {
        const inputArguments = expressions.map((e) => e.returnType);
        // addDays(toDate('2016-08-08'), 1) -> toDate('2016-08-09') (date value)
        if (inputArguments[0] && DataFlowTypesUtils.isDateType(inputArguments[0])) {
            return DataFlowTypesUtils.createType('date');
        } else if (inputArguments[0] && DataFlowTypesUtils.isTimeStampType(inputArguments[0])) {
            return DataFlowTypesUtils.createType('timestamp');
        }
        return undefined;
    }

    public static SPLIT_ARRAY_HANDLER(): IDataFlowDataType {
        const arrayDataFlowType = new ArrayDataFlowType();
        arrayDataFlowType.arrayType = new StringDataFlowType();
        return arrayDataFlowType;
    }

    public static SLICE_ARRAY_HANDLER(expressions: Expression[]): IDataFlowDataType {
        const arrayDataFlowType = new ArrayDataFlowType();
        const inputArguments = expressions.map((e) => e.returnType);
        if (inputArguments[0] && inputArguments[0] instanceof ArrayDataFlowType) {
            arrayDataFlowType.arrayType = (inputArguments[0] as ArrayDataFlowType).arrayType;
        }
        return arrayDataFlowType;
    }

    public static HIGHER_ORDER_ARRAY_HANDLER(expressions: Expression[]): IDataFlowDataType {
        const inputArguments = expressions.map((e) => e.returnType);
        return inputArguments[0] && inputArguments[0] instanceof ArrayDataFlowType
            ? DataFlowExpressionFunctions.SLICE_ARRAY_HANDLER(expressions)
            : new AnyDataFlowType();
    }

    public static HIGHER_ORDER_SINGLE_HANDLER(expressions: Expression[]): IDataFlowDataType {
        const inputArguments = expressions.map((e) => e.returnType);
        const arrayArg = inputArguments && inputArguments[0];
        return arrayArg instanceof ArrayDataFlowType ? DataFlowTypesUtils.cloneType(arrayArg.arrayType) : new AnyDataFlowType();
    }

    public static HIGHER_ORDER_MAP_LOOP(expressions: Expression[]): IDataFlowDataType {
        const arrayDataFlowType = new ArrayDataFlowType();
        const inputArgumentsTypes = expressions.map((e) => e.returnType);
        if (inputArgumentsTypes[1]) {
            arrayDataFlowType.arrayType = DataFlowTypesUtils.cloneType(inputArgumentsTypes[1]);
        }
        return arrayDataFlowType;
    }

    public static HIGHER_ORDER_MAP_ASSOCIATE(expressions: Expression[]): IDataFlowDataType {
        const inputArgumentsTypes = expressions.map((e) => e.returnType);

        if (!(inputArgumentsTypes[0] instanceof MapDataFlowType)) {
            return new AnyDataFlowType();
        } else if (!inputArgumentsTypes[1]) {
            return new AnyDataFlowType();
        }

        const arrayDataFlowType = new ArrayDataFlowType();
        arrayDataFlowType.arrayType = DataFlowTypesUtils.cloneType(inputArgumentsTypes[1]);
        return arrayDataFlowType;
    }

    public static TRANSFORM_FUNCTION_HANDLER(expressions: Expression[]): IDataFlowDataType {
        const inputArguments = expressions.map((e) => e.returnType);
        const arrayArg = inputArguments && inputArguments[0];
        return arrayArg instanceof ArrayDataFlowType ? DataFlowTypesUtils.cloneType(arrayArg) : new AnyDataFlowType();
    }

    public static TRANSFORM_FUNCTION_SINGLE_HANDLER(expressions: Expression[]): IDataFlowDataType {
        const inputArguments = expressions.map((e) => e.returnType);
        const arrayArg = inputArguments && inputArguments[0];
        return arrayArg instanceof ArrayDataFlowType ? DataFlowTypesUtils.cloneType(arrayArg.arrayType) : new AnyDataFlowType();
    }

    public static REDUCE_HANDLER(expressions: Expression[]): IDataFlowDataType {
        const inputArguments = expressions.map((e) => e.returnType);
        return inputArguments[1] ? DataFlowTypesUtils.cloneType(inputArguments[1]) : new AnyDataFlowType();
    }

    public static KEYVALUES_ARGUMENTS_VALIDATION(expressions: Expression[]): IErrorExpression[] {
        const violations: IErrorExpression[] = [];

        if (expressions.length < 2) {
            return violations;
        }

        const inputArguments = expressions.map((e) => e.returnType);
        if (!(inputArguments[0] instanceof ArrayDataFlowType && inputArguments[1] instanceof ArrayDataFlowType)) {
            // this should already be taken care of by arg type check, double checking to be sure
            return violations;
        }

        const typeViolations = DataFlowExpressionFunctions.ARGUMENTS_TYPEMATCH_VALIDATION([expressions[0]]);
        typeViolations.push(...DataFlowExpressionFunctions.ARGUMENTS_TYPEMATCH_VALIDATION([expressions[1]]));

        return typeViolations;
    }

    public static KEYVALUES_OUTPUT_FILTER(expressions: Expression[]): IDataFlowDataType {
        if (expressions.length < 2) {
            return DataFlowTypesUtils.createType('any');
        }

        const keyType = (expressions[0].returnType as ArrayDataFlowType).arrayType;
        const valueType = (expressions[1].returnType as ArrayDataFlowType).arrayType;

        return new MapDataFlowType(keyType, valueType);
    }

    public static ASSERT_ERROR_MESSAGES_OUTPUT_FILTER(expressions: Expression[]): IDataFlowDataType {
        return new MapDataFlowType(new StringDataFlowType(), new StringDataFlowType());
    }

    public static LOGICAL_IIF_HANDLER(expressions: Expression[]): IDataFlowDataType {
        return DataFlowExpressionFunctions.LOGICAL_IIF_BASE(expressions, false) as IDataFlowDataType;
    }

    public static LOGICAL_IIF_VALIDATOR(expressions: Expression[]): IErrorExpression[] {
        return DataFlowExpressionFunctions.LOGICAL_IIF_BASE(expressions, true) as IErrorExpression[];
    }

    public static LOGICAL_CASE_HANDLER(expressions: Expression[]): IDataFlowDataType {
        return DataFlowExpressionFunctions.LOGICAL_CASE_BASE(expressions, false) as IDataFlowDataType;
    }

    public static LOGICAL_CASE_VALIDATOR(expressions: Expression[]): IErrorExpression[] {
        return DataFlowExpressionFunctions.LOGICAL_CASE_BASE(expressions, true) as IErrorExpression[];
    }

    public static VALIDATE_ADD_MINUS_ARGS(expressions: Expression[], isMinus: boolean = false): IErrorExpression[] {
        const inputArguments = expressions.map((e) => e.returnType);
        const violations: IErrorExpression[] = [];
        const leftExpression = expressions[0];
        const rightExpression = expressions[1];
        if (inputArguments[0] && inputArguments[0].typeString && inputArguments[1] && inputArguments[1].typeString) {
            const returnType = DataFlowTypesUtils.getWiderType(leftExpression.returnType, rightExpression.returnType);
            if (returnType) {
                if (DataFlowTypesUtils.isNumberType(returnType) || (!isMinus && DataFlowTypesUtils.isStringType(returnType))) {
                    return undefined;
                } else if (isMinus && DataFlowTypesUtils.isStringType(returnType)) {
                    violations.push({
                        expression: rightExpression,
                        errorMessage: format(stringResources.DataFlowEditorErrorWrongTypeExpression, StringDataFlowType.TYPE),
                        treatErrorAsWarning: false
                    });
                    return violations;
                }
            }
            if (DataFlowTypesUtils.isBooleanType(leftExpression.returnType)) {
                violations.push({
                    expression: leftExpression,
                    errorMessage: format(stringResources.DataFlowEditorErrorWrongTypeExpression, BooleanDataFlowType.TYPE),
                    treatErrorAsWarning: false
                });
                return violations;
            } else if (DataFlowTypesUtils.isDateTimeType(leftExpression.returnType)) {
                if (!DataFlowTypesUtils.isIntegerType(rightExpression.returnType)) {
                    if (isMinus && DataFlowTypesUtils.isDateTimeType(rightExpression.returnType)) {
                        return undefined;
                    }
                    violations.push({
                        expression: rightExpression,
                        errorMessage: format(stringResources.DataFlowEditorErrorTypeMismatch,
                            rightExpression.returnType.typeString, IntegerDataFlowType.TYPE),
                        treatErrorAsWarning: false
                    });
                    return violations;
                }
            } else if (DataFlowTypesUtils.isNumberType(leftExpression.returnType) || DataFlowTypesUtils.isNumberType(rightExpression.returnType)
                || DataFlowTypesUtils.isStringType(leftExpression.returnType) || DataFlowTypesUtils.isStringType(rightExpression.returnType)) {
                violations.push({
                    expression: rightExpression,
                    errorMessage: format(stringResources.DataFlowEditorErrorTypeUncompatibleWithLeftSide,
                        rightExpression.returnType.typeString, leftExpression.returnType.typeString),
                    treatErrorAsWarning: false
                });
                return violations;
            }
        }
        return undefined;
    }

    public static VALIDATE_MUL_DIV_MOD_ARGS(expressions: Expression[]): IErrorExpression[] {
        const inputArguments = expressions.map((e) => e.returnType);
        const violations: IErrorExpression[] = [];
        if (inputArguments[0] && inputArguments[0].typeString && inputArguments[1] && inputArguments[1].typeString) {
            if (!(DataFlowTypesUtils.isNumberType(expressions[0].returnType) || expressions[0].returnType instanceof NullDataFlowType)
                || !(DataFlowTypesUtils.isNumberType(expressions[1].returnType) || expressions[1].returnType instanceof NullDataFlowType)) {
                violations.push({
                    expression: expressions[1],
                    errorMessage: format(stringResources.DataFlow_ShouldReturnSameType, inputArguments[0].typeString),
                    treatErrorAsWarning: false
                });
                return violations;
            }
        }
        return undefined;
    }

    public static VALIDATE_EQUALITY_TYPE_ARGS(expressions: Expression[]): IErrorExpression[] {
        const inputArguments = expressions.map((e) => e.returnType);
        const violations: IErrorExpression[] = [];
        const leftExpression = expressions[0];
        const rightExpression = expressions[1];
        if (inputArguments[0] && inputArguments[0].typeString && inputArguments[1] && inputArguments[1].typeString) {
            const directTypeMatch = leftExpression.returnType.typeString === rightExpression.returnType.typeString;
            const numericTypeMatch = DataFlowTypesUtils.isNumberType(leftExpression.returnType) && DataFlowTypesUtils.isNumberType(rightExpression.returnType);
            if (!directTypeMatch && !numericTypeMatch) {
                violations.push({
                    expression: rightExpression,
                    errorMessage: format(stringResources.DataFlowEditorErrorTypeMismatch,
                        rightExpression.returnType.typeString, leftExpression.returnType.typeString),
                    treatErrorAsWarning: false
                });
                return violations;
            }
        }
        return undefined;
    }

    public static VALIDATE_COMPARE_TYPE_ARGS(expressions: Expression[]): IErrorExpression[] {
        const inputArguments = expressions.map((e) => e.returnType);
        const leftExpression = expressions[0];
        const rightExpression = expressions[1];
        const violations: IErrorExpression[] = [];
        if (inputArguments[0] && inputArguments[0].typeString && inputArguments[1] && inputArguments[1].typeString) {
            const stringTypeMatch = DataFlowTypesUtils.isStringType(leftExpression.returnType) && DataFlowTypesUtils.isStringType(rightExpression.returnType);
            const dateTypeMatch = DataFlowTypesUtils.isDateType(leftExpression.returnType) && DataFlowTypesUtils.isDateType(rightExpression.returnType);
            const timeStampTypeMatch = DataFlowTypesUtils.isTimeStampType(leftExpression.returnType) && DataFlowTypesUtils.isTimeStampType(rightExpression.returnType);
            const numericTypeMatch = DataFlowTypesUtils.isNumberType(leftExpression.returnType) && DataFlowTypesUtils.isNumberType(rightExpression.returnType);
            if (!stringTypeMatch && !dateTypeMatch && !timeStampTypeMatch && !numericTypeMatch) {
                violations.push({
                    expression: rightExpression,
                    errorMessage: format(stringResources.DataFlowEditorErrorComparisonTypeMismatch,
                        leftExpression.returnType.typeString, rightExpression.returnType.typeString),
                    treatErrorAsWarning: false
                });
                return violations;
            }
        }
        return undefined;
    }

    private static VALIDATE_TO_DATE_TIMESTAMP_HANDLER(isToDate: boolean): (expressions: Expression[]) => IErrorExpression[] {
        const specialType = isToDate ? 'timestamp' : 'date';
        return (expressions: Expression[]) => {
            const inputArguments = expressions.map((e) => e.returnType);
            const violations: IErrorExpression[] = [];
            const firstDataType = inputArguments[0];
            const leftExpression = expressions[0];
            const rightExpression = expressions[1];
            const functionName = isToDate ? DataFlowExpressionFunctions.TO_DATE.name : DataFlowExpressionFunctions.TO_TIMESTAMP.name;
            if (firstDataType && firstDataType.typeString) {
                switch (firstDataType.typeString) {
                    case specialType:
                        if (inputArguments.length === 2) {
                            violations.push({
                                expression: rightExpression,
                                errorMessage: format(stringResources.DataflowEditorErrorDoesNotAcceptFormat, specialType),
                                treatErrorAsWarning: false
                            });
                            return violations;
                        }
                        break;
                    case 'string': case 'integer': case 'long': case 'null':
                        break;
                    default:
                        violations.push({
                            expression: leftExpression,
                            errorMessage: format(stringResources.DataflowEditorErrorWrongTypeParameter_Generic, functionName, firstDataType.typeString),
                            treatErrorAsWarning: false
                        });
                        return violations;
                }
            }
            return undefined;
        };
    }

    private static LOGICAL_IIF_BASE(expressions: Expression[], validate: boolean = false): IDataFlowDataType | IErrorExpression[] {
        const inputArguments = expressions.map((e) => e.returnType);
        const violations: IErrorExpression[] = [];
        if (inputArguments[1] && inputArguments[1].typeString) {
            if (inputArguments[2] && inputArguments[2].typeString !== inputArguments[1].typeString) {
                if (validate) {
                    violations.push({
                        expression: expressions[2],
                        errorMessage: format(stringResources.DataFlow_ShouldReturnSameType, inputArguments[1].typeString),
                        treatErrorAsWarning: false
                    });
                } else {
                    return undefined;
                }
            }
            return validate ? violations : DataFlowTypesUtils.getWidestType([inputArguments[1], inputArguments[2]]);
        }
        return validate ? violations : undefined;
    }

    private static LOGICAL_CASE_BASE(expressions: Expression[], validate: boolean = false): IDataFlowDataType | IErrorExpression[] {
        const inputArguments = expressions.map((e) => e.returnType);
        const violations: IErrorExpression[] = [];
        // Every even argument except the last should be conditional
        if (inputArguments.length > 0) {
            for (let i = 0; i < inputArguments.length - 1; i = i + 2) {
                if (inputArguments[i] && inputArguments[i].typeString !== 'boolean') {
                    if (validate) {
                        violations.push({
                            expression: expressions[i],
                            errorMessage: stringResources.DataFlow_ShouldReturnBoolean,
                            treatErrorAsWarning: false
                        });
                    } else {
                        return undefined;
                    }
                }
            }
        }
        let expressionReturnTypeString: string;
        const inputArgumentsWithReturnValue: IDataFlowDataType[] = [];
        if (inputArguments.length > 1) {
            for (let i = 1; i < inputArguments.length; i = i + 2) {
                if (inputArguments[i]) {
                    if (expressionReturnTypeString === undefined) {
                        expressionReturnTypeString = inputArguments[i].typeString;
                        if (inputArguments[i].typeString !== undefined) {
                            inputArgumentsWithReturnValue.push(inputArguments[i]);
                        }
                    } else {
                        if (inputArguments[i].typeString !== undefined) {
                            if (expressionReturnTypeString !== inputArguments[i].typeString) {
                                if (validate) {
                                    violations.push({
                                        expression: expressions[i],
                                        errorMessage: format(stringResources.DataFlow_ShouldReturnSameType, expressionReturnTypeString),
                                        treatErrorAsWarning: false
                                    });
                                } else {
                                    return undefined;
                                }
                            } else {
                                expressionReturnTypeString = inputArguments[i].typeString;
                                inputArgumentsWithReturnValue.push(inputArguments[i]);
                            }
                        }
                    }
                }
            }
            // Check last argument which should match return types.
            if (inputArguments[inputArguments.length - 1] && inputArguments[inputArguments.length - 1].typeString !== undefined) {
                if (inputArguments[inputArguments.length - 1].typeString === expressionReturnTypeString) {
                    inputArgumentsWithReturnValue.push(inputArguments[inputArguments.length - 1]);
                } else {
                    if (validate) {
                        violations.push({
                            expression: expressions[inputArguments.length - 1],
                            errorMessage: format(stringResources.DataFlow_ShouldReturnSameType, expressionReturnTypeString),
                            treatErrorAsWarning: false
                        });
                    } else {
                        return undefined;
                    }
                }
            }
        }
        return validate ? violations : DataFlowTypesUtils.getWidestType(inputArgumentsWithReturnValue);
    }

    private static VALIDATE_ARGUMENT_POSITIVE_INTEGER(expressions: Expression[], errorMessage: string, minValue?: number, maxValue?: number): IErrorExpression[] {
        const violations: IErrorExpression[] = [];
        if (expressions[0]) {
            const position = parseInt(expressions[0].text, 10);
            if ((isNaN(position) && !DataFlowTypesUtils.isIntegerType(expressions[0].returnType)) ||
                (minValue !== undefined && position < minValue) || (maxValue !== undefined && position > maxValue)) {
                violations.push({
                    expression: expressions[0],
                    errorMessage,
                    treatErrorAsWarning: false
                });
            }
        }
        return violations;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static UNFOLDED_ARRAY_FUNCTION_VALIDATION(functionRef: Expression, parentContext: any): IErrorExpression[] {
        const violations: IErrorExpression[] = [];
        if (!parentContext) {
            violations.push({
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                expression: functionRef.functionExpression as any,
                errorMessage: format(stringResources.DataFlowExpressionByNamesColumnsError, functionRef.functionExpression.basicName.text),
                treatErrorAsWarning: true
            });
        }
        return violations;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static TRANSFORM_FUNCTION_LOOKUP_ARGS_VALIDATION(functionName: string, expressions: Expression[], cacheSink: DataFlowSink): IErrorExpression[] {
        const violations: IErrorExpression[] = [];
        const keyColumns = cacheSink.keyColumns.filter((key) => !!key.expression);
        if (!keyColumns.length) {
            violations.push({
                expression: null,
                errorMessage: format(stringResources.DataFlowEditorErrorWrongInvalidFunction, stringResources.Add.toLowerCase(), functionName),
                treatErrorAsWarning: false
            });
        }
        if (expressions.length !== keyColumns.length) {
            violations.push({
                expression: null,
                errorMessage: format(stringResources.DataFlowEditorErrorWrongNumberOfParameters, `${cacheSink.name}#${functionName}`, `${cacheSink.keyColumns.length}`),
                treatErrorAsWarning: false
            });
        }
        expressions.forEach((expression, index) => {
            if (index < cacheSink.keyColumns.length) {
                if (expression.returnType && expression.returnType.typeString !== cacheSink.keyColumns[index].returnType.typeString
                    && cacheSink.keyColumns[index].returnType.typeString !== AnyDataFlowType.TYPE) {
                    violations.push({
                        expression: expressions[index],
                        errorMessage: format(stringResources.DataFlowEditorErrorWrongTypeOfParameter, `${cacheSink.name}#${functionName}`, `${index + 1}`,
                            `${cacheSink.keyColumns[index].returnType.typeString}`),
                        treatErrorAsWarning: false
                    });
                }
            }
        });
        return violations;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static TRANSFORM_FUNCTION_OUTPUTS_ARGS_VALIDATION(functionName: string, expressions: Expression[], cacheSink: DataFlowSink): IErrorExpression[] {
        const violations: IErrorExpression[] = [];
        const keyColumns = cacheSink.keyColumns.filter((key) => !!key.expression);
        if (keyColumns.length) {
            violations.push({
                expression: null,
                errorMessage: format(stringResources.DataFlowEditorErrorWrongInvalidFunction, stringResources.Remove.toLowerCase(), functionName),
                treatErrorAsWarning: false
            });
        }
        return violations;
    }

    private static NO_COLUMN_ARG_VALIDATION(checkFirst: boolean, expressions: Expression[]): IErrorExpression[] {
        const violations: IErrorExpression[] = [];
        const expressionsToCheck = [...expressions];
        if (!checkFirst) {
            expressionsToCheck.shift();
        }
        let containsColumnReference = false;
        while (expressionsToCheck.length && !containsColumnReference) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const currentExpression = expressionsToCheck.shift() as any;
            if (currentExpression instanceof ColumnOperand || (currentExpression.value && currentExpression.value instanceof ColumnOperand)) {
                containsColumnReference = true;
            }
            if (currentExpression.value && currentExpression.value.children) {
                expressionsToCheck.push(...currentExpression.value.children);
            }
        }
        if (containsColumnReference) {
            violations.push({
                expression: expressions[0],
                errorMessage: stringResources.DataFlowExpressionColumnUsedError,
                treatErrorAsWarning: false
            });
        }
        return violations;
    }

    private static BY_POSITION_FUNCTION_VALIDATION(expressions: Expression[]): IErrorExpression[] {
        const violations = [
            ...DataFlowExpressionFunctions.VALIDATE_ARGUMENT_POSITIVE_INTEGER(expressions, stringResources.Expression_Function_ByPosition_ValidationMessage, 1),
            ...DataFlowExpressionFunctions.NO_COLUMN_ARG_VALIDATION(true, expressions)
        ];
        return violations;
    }

    private static ESCAPE_UNESCAPE_VALIDATION(expressions: Expression[]): IErrorExpression[] {
        const violations: IErrorExpression[] = [];
        const supportedEscapeFormats = [`'json'`, `'xml'`, `'ecmascript'`, `'html'`, `'java'`];
        // Just validate the format in case we are provided a literal string. If not, just fallback to type validation logic.
        if (expressions && expressions[1] && expressions[1].value instanceof Literal && expressions[1].value.value instanceof StringLiteral) {
            if (!supportedEscapeFormats.some((f) => f === expressions[1].text)) {
                violations.push({
                    expression: expressions[1],
                    errorMessage: format(stringResources.DataflowEditorEscapeFunctionFormatNotAccepted, expressions[1].text),
                    treatErrorAsWarning: false
                });
            }
        }
        return violations;
    }

    private static AT_ARGUMENTS_VALIDATION(expressions: Expression[]): IErrorExpression[] {
        const violations: IErrorExpression[] = [];

        const inputArguments = expressions.map((e) => e.returnType);
        if (inputArguments.length === 0) {
            return violations;
        }

        // Make sure first parameter is either an array or a map
        if (inputArguments[0].typeString !== ArrayDataFlowType.TYPE && inputArguments[0].typeString !== MapDataFlowType.TYPE) {
            violations.push({
                expression: expressions[0],
                errorMessage: stringResources.DataFlow_AtFunctionArgumentsTypeViolation,
                treatErrorAsWarning: false
            });
        }

        // Make sure second parameter is either an integer or the map key type
        if (inputArguments.length === 2) {
            if (inputArguments[0].typeString === ArrayDataFlowType.TYPE && inputArguments[1].typeString !== IntegerDataFlowType.TYPE) {
                violations.push({
                    expression: expressions[1],
                    errorMessage: stringResources.DataFlow_AtFunctionSecondParameterViolationArray,
                    treatErrorAsWarning: false
                });
            } else if (inputArguments[0].typeString === MapDataFlowType.TYPE) {
                const mapType = inputArguments[0] as MapDataFlowType;
                if (inputArguments[1].typeString !== mapType.mapKeyTypeString) {
                    violations.push({
                        expression: expressions[1],
                        errorMessage: stringResources.DataFlow_AtFunctionSecondParameterViolationMap,
                        treatErrorAsWarning: false
                    });
                }
            }
        }

        return violations;
    }

    private static AT_OUTPUT_FILTER(expressions: Expression[]): IDataFlowDataType {
        const inputArgument = expressions[0];
        if (!inputArgument) {
            // will return any[]
            return DataFlowTypesUtils.createType('any');
        } else if (inputArgument.returnType.typeString === ArrayDataFlowType.TYPE) {
            const arrDataFlowType = DataFlowExpressionFunctions.SLICE_ARRAY_HANDLER(expressions) as ArrayDataFlowType;
            return arrDataFlowType.arrayType;
        } else if (inputArgument.returnType.typeString === MapDataFlowType.TYPE) {
            const mapType = inputArgument.returnType as MapDataFlowType;
            return mapType.mapValueType;
        }

        return DataFlowTypesUtils.createType('any');
    }

    private static ASSOCIATE_ARGUMENTS_VALIDATION(expressions: Expression[]): IErrorExpression[] {
        const violations: IErrorExpression[] = [];

        if (expressions.length % 2 !== 0) {
            violations.push({
                expression: expressions[0],
                errorMessage: stringResources.DataFlow_AssociateFunctionParameterCountViolation,
                treatErrorAsWarning: false
            });
        } else {
            // odd parameters should match in type, as with even
            const inputArguments = expressions.map((e) => e.returnType);
            const keyType = inputArguments[0];
            const valueType = inputArguments[1];

            let valid: boolean = true;
            let i = 0;
            for (; i < inputArguments.length; i++) {
                if (i % 2 === 0) {
                    if (inputArguments[i].typeString !== keyType.typeString) {
                        valid = false;
                        break;
                    }
                } else {
                    if (inputArguments[i].typeString !== valueType.typeString) {
                        valid = false;
                        break;
                    }
                }
            }

            if (!valid) {
                violations.push({
                    expression: expressions[i],
                    errorMessage: stringResources.DataFlow_AssociateFunctionParameterTypeMatchViolation,
                    treatErrorAsWarning: false
                });
            }

        }

        return violations;
    }

    private static ASSOCIATE_OUTPUT_FILTER(expressions: Expression[]): IDataFlowDataType {
        if (expressions.length < 2) {
            return DataFlowTypesUtils.createType('any');
        }

        const inputArguments = expressions.map((e) => e.returnType);
        return new MapDataFlowType(inputArguments[0], inputArguments[1]);
    }

    private static REASSOCIATE_OUTPUT_FILTER(expressions: Expression[]): IDataFlowDataType {
        if (expressions.length < 2) {
            return DataFlowTypesUtils.createType('any');
        }

        return expressions[0].returnType;
    }

    private static ARRAY_OUTPUT_FILTER(expressions: Expression[]): IDataFlowDataType {
        const inputArguments = expressions.map((e) => e.returnType);
        if (inputArguments.length === 0) {
            // will return any[]
            return DataFlowTypesUtils.createType('array');
        }

        const widestType = DataFlowTypesUtils.getWidestType(inputArguments);
        if (widestType.typeString !== AnyDataFlowType.TYPE) {
            return DataFlowTypesUtils.createType(widestType.typeString + '[]');
        }

        return DataFlowTypesUtils.createType('array');
    }

    private static ARRAY_TYPEMATCH_VALIDATION(expressions: Expression[]): IErrorExpression[] {
        const violations: IErrorExpression[] = [];

        const inputArguments = expressions.map((e) => e.returnType);

        if (inputArguments.length === 0 || !inputArguments[0]) {
            return violations;
        }

        // make sure we are dealing with arrays
        if (!(inputArguments.every((arg) => arg instanceof ArrayDataFlowType))) {
            return violations;
        }

        const arrType = (inputArguments[0] as ArrayDataFlowType).arrayTypeString;
        const arrTypesMatch = inputArguments.every((arg) => (arg as ArrayDataFlowType).arrayTypeString === arrType);

        if (!arrTypesMatch) {
            violations.push({
                expression: expressions[0],
                errorMessage: stringResources.DataFlow_ArgumentsReturnSameType,
                treatErrorAsWarning: false
            });
        }

        return violations;
    }

    private static ARGUMENTS_TYPECOMPATIBLE_VALIDATION(expressions: Expression[]): IErrorExpression[] {
        const violations: IErrorExpression[] = [];

        const inputArguments = expressions.map((e) => e.returnType);
        if (inputArguments.length === 0 || !inputArguments[0]) {
            return violations;
        }
        if (DataFlowTypesUtils.getWidestType(inputArguments).typeString === 'any') {
            violations.push({
                expression: expressions[0],
                errorMessage: stringResources.DataFlow_ArgumentsReturnSameType,
                treatErrorAsWarning: true
            });
        }
        return violations;
    }

    private static ARGUMENTS_TYPEMATCH_VALIDATION(expressions: Expression[]): IErrorExpression[] {
        const violations: IErrorExpression[] = [];

        const inputArguments = expressions.map((e) => e.returnType);
        if (inputArguments.length === 0 || !inputArguments[0]) {
            return violations;
        }

        const arrType = inputArguments[0];
        if (!inputArguments.every((arg) => (arg && arg.typeString === arrType.typeString))) {
            violations.push({
                expression: expressions[0],
                errorMessage: stringResources.DataFlow_ArgumentsReturnSameType,
                treatErrorAsWarning: true
            });
        }

        return violations;
    }

    private static FLATTEN_ARGUMENTS_VALIDATION(expressions: Expression[]): IErrorExpression[] {
        const violations: IErrorExpression[] = [];

        const inputArguments = expressions.map((e) => e.returnType);
        if (inputArguments.length === 0) {
            return violations;
        }

        // First argument must be an array, last argument can be a boolean
        if (!(inputArguments[0] instanceof ArrayDataFlowType)) {
            violations.push({
                expression: expressions[0],
                errorMessage: stringResources.DataFlow_FlattenFunctionParameterTypeViolation,
                treatErrorAsWarning: false
            });
        }

        const valid = inputArguments.every((dataType, idx) => {
            return idx === inputArguments.length - 1
                ? dataType instanceof ArrayDataFlowType || dataType instanceof BooleanDataFlowType
                : dataType instanceof ArrayDataFlowType;
        });

        if (!valid) {
            violations.push({
                expression: expressions[0],
                errorMessage: stringResources.DataFlow_FlattenFunctionParameterTypeViolation,
                treatErrorAsWarning: false
            });
        }

        return violations;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static PARENT_IS_TYPECAST_VALIDATION(functionRef: Expression, parentContext: any): IErrorExpression[] {
        const violations: IErrorExpression[] = [];
        const parentFunctionName = !!parentContext && !!parentContext.children[0] && !!parentContext.children[0].getText ? parentContext.children[0].getText() : undefined;
        if (!parentFunctionName || !DataFlowExpressionFunctions.TYPE_CASTING_FUNCTIONS.find((func) => func.name === parentFunctionName)) {
            violations.push({
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                expression: functionRef.functionExpression as any,
                errorMessage: stringResources.DataFlowExpressionTypecastWarning,
                treatErrorAsWarning: true
            });
        }
        return violations;
    }

    private static ROW_MATCHING_FUNCTION_VALIDATION(expressions: Expression[]): IErrorExpression[] {
        return DataFlowExpressionFunctions.VALIDATE_ARGUMENT_POSITIVE_INTEGER(expressions, stringResources.Expression_Function_RowMatch_ValidationMessage, 1, 2);
    }

    private static VALIDATE_FIRST_ARG_NO_COLUMN_REF(expressions: Expression[]): IErrorExpression[] {
        const violations: IErrorExpression[] = [];
        let referencesColumn = expressions[0] && expressions[0].value instanceof ColumnOperand;
        const toCheck = [expressions[0]];
        while (!referencesColumn && toCheck.length) {
            const currentExpression = toCheck.shift();
            if (currentExpression && currentExpression.value instanceof ColumnOperand) {
                referencesColumn = true;
            } else if (currentExpression && currentExpression.value instanceof FunctionExpression && currentExpression.value.children) {
                currentExpression.value.children.forEach((childExpression) => {
                    toCheck.push(childExpression);
                });
            }
        }
        if (referencesColumn) {
            violations.push({
                expression: expressions[0],
                errorMessage: stringResources.DataFlowNTileValidationError,
                treatErrorAsWarning: false
            });
            return violations;
        }
        return violations;
    }

    private static VALIDATE_NUMERIC_CONVERSION_FUNCTION(isLong: boolean, functionName: string): (expressions: Expression[]) => IErrorExpression[] {
        return (expressions: Expression[]) => {
            const inputArguments = expressions.map((e) => e.returnType);
            const violations: IErrorExpression[] = [];
            const firstDataType = inputArguments[0];
            if (firstDataType && firstDataType.typeString) {
                const generalCase = DataFlowTypesUtils.isNumberType(firstDataType) || DataFlowTypesUtils.isStringType(firstDataType) ||
                    firstDataType instanceof NullDataFlowType || firstDataType instanceof AnyDataFlowType;
                const longCase = isLong && DataFlowTypesUtils.isDateTimeType(firstDataType);
                if (!generalCase && !longCase) {
                    violations.push({
                        expression: expressions[0],
                        errorMessage: format(stringResources.DataflowEditorErrorWrongTypeParameter_Generic, functionName, firstDataType.typeString),
                        treatErrorAsWarning: false
                    });
                    return violations;
                }
            }
            return undefined;
        };
    }
}
